import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Container, AppBar, Toolbar, Accordion, AccordionSummary, AccordionDetails, Menu, MenuItem, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import BoltIcon from '@mui/icons-material/Bolt';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { pageBackgroundSx, brandColors, wordmarkFontFamily } from './theme';
import supportedPlatforms from './platforms';
import { apiJson } from './api/apiFetch';
import { SUPPORTED_LANGUAGES } from './i18n';

// "Neden Xpa-Migration" kartlarının ikonları — metin çeviriden geldiği için ikonlar burada
// sırayla sabit tutuluyor (i18n JSON'undaki "why.items" dizisiyle AYNI SIRADA olmalı).
const highlightIcons = [
  <PublicIcon sx={{ fontSize: 34 }} />,
  <ImageIcon sx={{ fontSize: 34 }} />,
  <PublicIcon sx={{ fontSize: 34 }} />,
  <SecurityIcon sx={{ fontSize: 34 }} />,
  <BoltIcon sx={{ fontSize: 34 }} />,
];

function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(false);
  const isLoggedIn = Boolean(localStorage.getItem('token') || localStorage.getItem('access_token'));
  const [brandMenuAnchor, setBrandMenuAnchor] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [myRole, setMyRole] = useState(null);

  useEffect(() => {
    if (isLoggedIn) {
      apiJson('/me').then((data) => setMyRole(data.role)).catch(() => setMyRole(null));
    }
    // eslint-disable-next-line
  }, [isLoggedIn]);

  const currentLanguage = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem('xpa_language', code);
    } catch (_) {
      // localStorage kapalıysa (gizli sekme vb.) sessizce geç — dil yine de o oturum için değişir.
    }
    setLangMenuAnchor(null);
  };

  const toNameFromEmail = (em) => {
    try {
      const local = String(em).split('@')[0] || '';
      if (!local) return '';
      const cleaned = local.replace(/[._-]+/g, ' ').trim();
      return cleaned
        .split(' ')
        .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : ''))
        .join(' ')
        .trim();
    } catch (_) {
      return '';
    }
  };

  const getDisplayName = () => {
    const n = localStorage.getItem('user_full_name') || localStorage.getItem('full_name');
    if (n && n.trim() !== '') return n;
    const emailStored = localStorage.getItem('user_email');
    if (emailStored && emailStored.includes('@')) {
      const pretty = toNameFromEmail(emailStored);
      if (pretty) return pretty;
    }
    const t2 = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!t2) return '';
    try {
      let s = t2.replace(/-/g, '+').replace(/_/g, '/');
      while (s.length % 4) s += '=';
      const decoded = atob(s);
      const parts = decoded.split(':');
      const emailFromToken = parts[0] || '';
      const pretty2 = toNameFromEmail(emailFromToken);
      return pretty2 || '';
    } catch (_) {
      return '';
    }
  };

  const handleStartTransferClick = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (token && String(token).trim() !== '') {
      navigate('/start-migration');
    } else {
      navigate('/register');
    }
  };

  // Platform sayısı platforms.js'den (supportedPlatforms.length) TEK kaynaktan geliyor —
  // bu sayı hiçbir çeviri dosyasında sabit metin olarak YAZILMIYOR, hepsi {{count}}
  // interpolasyonuyla buradan besleniyor (ileride platform sayısı değişince 9 dosyayı elle
  // güncellemeye gerek kalmasın diye — bkz. 2026-09-12'de bulunan "79 vs 78" tutarsızlığı).
  const platformCount = supportedPlatforms.length;
  const whyItemsRaw = t('why.items', { returnObjects: true });
  const whyItems = whyItemsRaw.map((item, idx) =>
    idx === 0 ? { ...item, title: t('why.items.0.title', { count: platformCount }) } : item
  );
  const faqItems = t('faq.items', { returnObjects: true });

  return (
    <Box sx={pageBackgroundSx}>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description', { count: platformCount })} />
        <meta property="og:title" content={t('meta.ogTitle')} />
        <meta property="og:description" content={t('meta.ogDescription', { count: platformCount })} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/`} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Xpa-Migration',
            url: window.location.origin,
            description: t('meta.description', { count: platformCount }),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Header */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ background: 'rgba(30,60,114,0.7)', borderBottom: 1, borderColor: '#fff' }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
            {/* Dil seçici — sol üstte, bayraklarla */}
            <Button
              onClick={(e) => setLangMenuAnchor(e.currentTarget)}
              sx={{ color: '#fff', fontWeight: 700, textTransform: 'none', minWidth: 0, px: 1 }}
              aria-label="Change language"
            >
              <img src={`/flags/${currentLanguage.flag}.svg`} alt="" width={22} height={16} style={{ marginRight: 8, borderRadius: 2, objectFit: 'cover' }} />
              <Typography component="span" sx={{ display: { xs: 'none', sm: 'inline' }, fontSize: 14 }}>
                {currentLanguage.label}
              </Typography>
            </Button>
            <Menu anchorEl={langMenuAnchor} open={Boolean(langMenuAnchor)} onClose={() => setLangMenuAnchor(null)}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  selected={lang.code === i18n.language}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <img src={`/flags/${lang.flag}.svg`} alt="" width={22} height={16} style={{ marginRight: 10, borderRadius: 2, objectFit: 'cover' }} /> {lang.label}
                </MenuItem>
              ))}
            </Menu>

            {isLoggedIn ? (
              <>
                <Button
                  onClick={(e) => setBrandMenuAnchor(e.currentTarget)}
                  sx={{ color: '#fff', fontWeight: 800, fontSize: '1.25rem', textTransform: 'none', p: 0.5 }}
                >
                  xpa-migration
                </Button>
                <Menu anchorEl={brandMenuAnchor} open={Boolean(brandMenuAnchor)} onClose={() => setBrandMenuAnchor(null)}>
                  <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 700, color: brandColors.blueDark }}>
                    {getDisplayName() || t('nav.myAccount')}
                  </MenuItem>
                  <Divider />
                  {(myRole === 'admin' || myRole === 'super_admin') && (
                    <MenuItem onClick={() => { setBrandMenuAnchor(null); navigate('/admin'); }}>
                      {t('nav.adminPanel')}
                    </MenuItem>
                  )}
                </Menu>
              </>
            ) : (
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>xpa-migration</Typography>
            )}
            <Button component={RouterLink} to="/blog" sx={{ color: '#fff', fontWeight: 600, ml: { xs: 0.5, sm: 2 } }}>{t('nav.blog')}</Button>
          </Box>
          {isLoggedIn ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#fff' }}>
                {getDisplayName()}
              </Typography>
              <Button variant="outlined" size="small" sx={{ color: '#fff', borderColor: '#fff', fontWeight: 700 }} onClick={() => navigate('/settings')}>
                {t('nav.settings')}
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
              <Button variant="contained" sx={{ background: '#fff', color: brandColors.blueDark, fontWeight: 700, boxShadow: 'none' }} onClick={() => navigate('/register')}>{t('nav.register')}</Button>
              <Button variant="outlined" sx={{ color: '#fff', borderColor: '#fff', fontWeight: 700 }} onClick={() => navigate('/login')}>{t('nav.login')}</Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Box sx={{
            bgcolor: 'rgba(30,60,114,0.92)',
            borderRadius: 4,
            boxShadow: 6,
            px: { xs: 2, sm: 6 },
            py: { xs: 3, sm: 5 },
            maxWidth: 700,
            width: '100%',
            textAlign: 'center',
            border: '1px solid #fff',
          }}>
            <Typography
              sx={{
                fontFamily: wordmarkFontFamily,
                fontWeight: 900,
                fontSize: { xs: 44, sm: 68 },
                letterSpacing: 0.5,
                lineHeight: 1,
                color: '#fff',
                textAlign: 'center',
                mb: 1.5,
              }}
            >
              xpa-migration
            </Typography>
            <Typography
              sx={{
                fontFamily: wordmarkFontFamily,
                fontWeight: 600,
                fontSize: { xs: 20, sm: 26 },
                color: '#fff',
                textAlign: 'center',
                opacity: 0.95,
                mb: 3,
                letterSpacing: 0.5,
              }}
            >
              {t('hero.tagline')}
            </Typography>
            <Typography variant="h6" color="#fff" sx={{ mb: 3, textAlign: 'center', opacity: 0.95, fontWeight: 500 }}>
              {t('hero.subtitle')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              color="success"
              sx={{ px: 6, py: 2, fontSize: 20, fontWeight: 700, mt: 2, background: brandColors.green }}
              onClick={handleStartTransferClick}
            >
              {t('hero.cta')}
            </Button>
          </Box>
        </Box>

        {/* Neden Xpa-Migration */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 4, boxShadow: 6, px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 }, maxWidth: 1000, width: '100%', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3, textAlign: 'center', color: brandColors.blueDark }}>
              {t('why.heading')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3 }}>
              {whyItems.map((h, idx) => (
                <Box key={h.title} sx={{ width: { xs: '100%', sm: 220 }, textAlign: 'center', color: brandColors.blueDark }}>
                  <Box sx={{ color: brandColors.red, mb: 1 }}>{highlightIcons[idx]}</Box>
                  <Typography fontWeight={700} sx={{ mb: 0.5 }}>{h.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>{h.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Bağlı Mağazalar — yeni ek hizmet */}
        <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.97)', borderRadius: 4, boxShadow: 6,
            px: { xs: 3, sm: 6 }, py: { xs: 4, sm: 5 }, maxWidth: 900, width: '100%',
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3,
            border: `2px solid ${brandColors.green}`,
          }}>
            <SyncAltIcon sx={{ fontSize: 56, color: brandColors.green, flexShrink: 0 }} />
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: brandColors.blueDark, mb: 0.5 }}>
                {t('connectedStores.heading')}
              </Typography>
              <Typography sx={{ color: brandColors.blueDark, opacity: 0.85 }}>
                {t('connectedStores.text')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: brandColors.green, fontWeight: 700, px: 4, whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={() => navigate('/magazalarim')}
            >
              {t('connectedStores.cta')}
            </Button>
          </Box>
        </Box>

        {/* Desteklenen Platformlar */}
        <Box sx={{ mb: 4, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 4, boxShadow: 6, px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 }, maxWidth: 900, width: '100%', textAlign: 'center', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2, letterSpacing: 2, color: brandColors.blueDark }}>
              {t('platforms.heading', { count: supportedPlatforms.length })}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.2, mb: 2 }}>
              {supportedPlatforms.map((name) => (
                <Box key={name} sx={{ px: 2, py: 1, bgcolor: 'rgba(30,60,114,0.08)', borderRadius: 2, color: brandColors.blueDark, fontWeight: 600, fontSize: 14, border: '1px solid #e0e0e0' }}>
                  {name}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Biz Kimiz */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 4, boxShadow: 6, px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 }, maxWidth: 900, width: '100%', textAlign: 'center', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: brandColors.blueDark }}>
              {t('about.heading')}
            </Typography>
            <Box sx={{ maxWidth: 700, mx: 'auto', textAlign: 'center', color: brandColors.blueDark, opacity: 0.92, fontSize: 18, fontWeight: 400 }}>
              {t('about.text')}
            </Box>
          </Box>
        </Box>

        {/* SSS */}
        <Box sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 4, boxShadow: 6, px: { xs: 2, sm: 6 }, py: { xs: 3, sm: 4 }, maxWidth: 900, width: '100%', border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2, textAlign: 'center', color: brandColors.blueDark }}>
              {t('faq.heading')}
            </Typography>
            {faqItems.map((item, idx) => (
              <Accordion
                key={item.q}
                expanded={expandedFaq === idx}
                onChange={() => setExpandedFaq(expandedFaq === idx ? false : idx)}
                sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid #e0e0e0' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600} sx={{ color: brandColors.blueDark }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: brandColors.blueDark, opacity: 0.85 }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* Kurucular ve İletişim */}
        <Box sx={{ mb: 8, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'center', alignItems: 'stretch', gap: 6 }}>
          <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: 4, p: 4, minWidth: 260, maxWidth: 400, color: brandColors.blueDark, boxShadow: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              {t('team.heading')}
            </Typography>
            <Typography fontWeight={600} fontSize={18}>
              {t('team.name')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: 4, p: 4, minWidth: 260, maxWidth: 400, color: brandColors.blueDark, boxShadow: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              {t('contact.heading')}
            </Typography>
            <Typography sx={{ opacity: 0.7, fontStyle: 'italic' }}>
              {t('contact.placeholder')}
            </Typography>
          </Box>
        </Box>

        {/* Footer — yasal bağlantılar */}
        <Box sx={{ textAlign: 'center', pb: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <RouterLink to="/kullanim-sartlari" style={{ color: '#fff', opacity: 0.8, fontSize: 14 }}>{t('footer.terms')}</RouterLink>
          <RouterLink to="/gizlilik-politikasi" style={{ color: '#fff', opacity: 0.8, fontSize: 14 }}>{t('footer.privacy')}</RouterLink>
        </Box>
      </Container>
    </Box>
  );
}

export default Home;
